import * as React from 'react'
import { closeInsertArticleModal, showContactModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import ModalMiddle from './Parts/ModalMiddle'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { updateSettings } from '../../store/authentication/actions'
import ModalLoader from './Parts/ModalLoader'
import { WithTranslation, withTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Style } from '../../styles'
import ModalWindow from './Parts/ModalWindow'
import { Article, CurrentUser, HelpCenter, PublicLocale, Settings } from '../../types'
import ArticlesController from '../../controllers/ArticlesController'
import { HelpCentersController } from '../../controllers'
import MobilityHelper from '../../helpers/MobilityHelper'
import HelpCenterHelper from '../../helpers/HelpCenterHelper'
import ModalFooter from './Parts/ModalFooter'
import PowerSelect from '../Form/PowerSelect'
import Utils from '../../utilities/Utils'
import Icon from '../Icons/Icon'
import Button from '../Button/Button'

const ArticleModalWindow = styled(ModalWindow)`
  max-width: 800px;
  min-width: 700px;
  max-height: 400px;
  min-height: 400px;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    width: 100%;
    height: 100;
    max-height: initial;
    max-width: initial;
  } 
`

const ArticleModalMiddle = styled(ModalMiddle)`
  display: flex;
  flex-direction: row;
  height: calc(400px - 56px);
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  overflow: hidden;
`

const Articles = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  border-right: 1px solid ${Style.color.border};
  padding: 8px;
  gap: 8px;
`

const ArticlesHeader = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: -8px;
  padding: 0 12px;
`

const ArticlesTitle = styled.div`
  background-color: white;
  color: #646462;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 0;
`

const ArticleItem = styled.div<{ selected?: boolean }>`
  color: #1a1a1a;
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #f8f8f7;
  }

  ${props => props.selected && css`
    background: #f8f8f7;
  `}
`

const Preview = styled.div`
  flex: 1.5;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    box-shadow: none;
  }
`

const LocaleContainer = styled.div`
  min-width: 250px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 12px;
  border-bottom: 1px solid rgb(245, 245, 245);
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    position: absolute;
    left: 4px;
    width: 16px;
    height: 16px;
  }
`

const Search = styled.input`
  width: 100%;
  font-weight: normal;
  font-size: 14px;
  color: rgb(20, 22, 26);
  padding: 10px 0px 10px 25px !important;
  height: 40px;
  width: 100%;
  border: none;
  outline: none;
`

interface IStateToProps {
  currentUser: CurrentUser
  helpCenterId: string
  locale?: PublicLocale
  onSubmit?: (helpCenterId: string, locale: PublicLocale, article: Article) => void
}

interface IDispatchToProps {
  updateSettings: typeof updateSettings
  close: typeof closeInsertArticleModal
  showContactModal: typeof showContactModal
}

type IProps = IDispatchToProps & IStateToProps & WithTranslation

interface IState {
  didInitialLoad: boolean
  currentPage: number,
  totalPages: number
  reachedEnd: boolean
  isFetching: boolean
  search: string
  helpCenter: HelpCenter
  articles: Article[]
  selectedLocale: PublicLocale
  selectedArticle: Article | null
}

class InsertArticleModal extends React.Component<IProps, IState> {
  private container = React.createRef<HTMLDivElement>()

  constructor(props: IProps) {
    super(props)

    this.state = {
      didInitialLoad: false,
      helpCenter: null,
      articles: [],
      selectedLocale: null,
      selectedArticle: null,
      currentPage: 0,
      totalPages: 0,
      reachedEnd: false,
      isFetching: false,
      search: ''
    }

    this.debouncedFetchArticles = Utils.debounce(this.fetchView.bind(this), 500)
    this.fetchView = this.fetchView.bind(this)
    this.onCloseModalClick = this.onCloseModalClick.bind(this)
    this.onArticleClick = this.onArticleClick.bind(this)
    this.onLocaleChange = this.onLocaleChange.bind(this)
    this.onScroll = Utils.debounce(this.onScroll.bind(this), 500)
    this.onSearchChange = this.onSearchChange.bind(this)
    this.scrollToTop = this.scrollToTop.bind(this)
  }

  componentDidMount() {
    this.fetchView()
  }

  debouncedFetchArticles(page: number) {
    this.fetchView(page)
  }

  async fetchView(page: number = 1) {
    const { helpCenterId, locale } = this.props
    const { search, articles: stateArticles, selectedLocale, selectedArticle } = this.state

    this.setState({ isFetching: true }, async () => {
      try {
        const { help_center } = await HelpCentersController.getForm({ id: helpCenterId })
        const response = await ArticlesController.getArticles(helpCenterId, {
          page: page,
          search: search,
        })
        const { articles: newArticles, current_page, total_pages, total_entries } = response;

        const articles = page === 1 ? [...newArticles] : [...stateArticles, ...newArticles]
        this.setState({
          helpCenter: help_center,
          articles: articles,
          selectedArticle: selectedArticle || (articles.length > 0 ? articles[0] : null),
          selectedLocale: (selectedLocale || help_center.locale) as PublicLocale,
          currentPage: current_page,
          totalPages: total_pages,
          didInitialLoad: true,
          isFetching: false,
          reachedEnd: current_page === total_pages
        }, () => {
          if (page === 1) this.scrollToTop()
        });
      } catch (ex) {
        console.error(ex)
      }
    })
  }

  scrollToTop() {
    if (this.container.current) {
      this.container.current.scrollTo({ top: 0 })
    }
  }

  onCloseModalClick() {
    this.props.close()
  }

  onArticleClick(article: Article) {
    this.setState({ selectedArticle: article })
  }

  onLocaleChange = (option) => {
    if (option) {
      this.setState({ selectedLocale: option.value })
    }
  }

  onFormSubmit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()

    const { helpCenter, selectedArticle, selectedLocale } = this.state
    const { onSubmit } = this.props

    if (onSubmit && selectedArticle) {
      this.props.onSubmit(helpCenter.id, selectedLocale, selectedArticle)
      this.props.close()
    }
  }

  onScroll(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    const { reachedEnd, isFetching } = this.state
    const container = this.container.current
    const scrollEndReached = container.scrollHeight - container.scrollTop <= container.clientHeight + 20

    if (scrollEndReached && !reachedEnd && !isFetching) {
      const { currentPage } = this.state
      this.fetchView(currentPage + 1)
    }
  }

  onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    this.setState({ search: value }, () => this.debouncedFetchArticles(1))
  }

  render() {
    const { t, currentUser } = this.props
    const {
      didInitialLoad,
      helpCenter,
      selectedLocale,
      selectedArticle,
      articles,
      search,
    } = this.state

    let supportedLocaleOptions: { label: string, value: PublicLocale }[] = []
    let selectedLocaleOption = null

    if (helpCenter) {
      supportedLocaleOptions = HelpCenterHelper.getSupportedLocaleOptions(helpCenter)
      selectedLocaleOption = supportedLocaleOptions.find(option => option.value === String(selectedLocale))
    }

    return (
      <ArticleModalWindow>
        <Content>
          <ModalHeader
            title={t('InsertArticleModal::Insert article')}
            onCloseClick={this.onCloseModalClick}
          />

          {!didInitialLoad && <ModalLoader />}
          {didInitialLoad && <ArticleModalMiddle>
            <Articles ref={this.container} onScroll={this.onScroll}>
              <ArticlesHeader>
                <ArticlesTitle>{t('InsertArticleModal::{{name}} Help Center', { name: helpCenter.name })}</ArticlesTitle>

                <SearchContainer>
                  <Icon icon='messenger-search' />
                  <Search
                    type='text'
                    placeholder={t('InsertArticleModal::Search articles')}
                    value={search}
                    onChange={this.onSearchChange}
                  />
                </SearchContainer>
              </ArticlesHeader>

              {articles.map((article) => (
                <ArticleItem
                  key={article.id}
                  selected={article.id === selectedArticle?.id}
                  onClick={() => this.onArticleClick(article)}
                >
                  {MobilityHelper.getTranslation(helpCenter.locale as PublicLocale, article.translations, 'title') || ''}
                </ArticleItem>
              ))}
            </Articles>
            <Preview>
              {selectedArticle && <iframe
                src={HelpCenterHelper.getArticleUrl(helpCenter, selectedArticle, selectedLocale, { embed: true })}
              />}
            </Preview>
          </ArticleModalMiddle>}
        </Content>

        <ModalFooter>
          <div className='modal-footer'>
            <div>
              <LocaleContainer>
                <PowerSelect
                  options={supportedLocaleOptions}
                  value={selectedLocaleOption}
                  onChange={this.onLocaleChange}
                  placeholder={t('HelpCenter::Select language')}
                />
              </LocaleContainer>
            </div>
            <div className='modal-footer-actions'>
              <div key='main-action' className='popover-wrapper'>
                <Button
                  type='success'
                  onClick={this.onFormSubmit}
                  disabled={!selectedArticle}
                  text={t('InsertArticleModal::Insert article')}
                />
              </div>
            </div>
          </div>
        </ModalFooter>
      </ArticleModalWindow>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    },
    modals: {
      insertArticleModal: {
        helpCenterId,
        locale,
        onSubmit,
      }
    }
  } = state

  return {
    helpCenterId: helpCenterId,
    locale: locale,
    onSubmit: onSubmit,
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    updateSettings: (settings: Settings) => dispatch(updateSettings(settings)),
    close: () => dispatch(closeInsertArticleModal()),
    showContactModal: (options) => dispatch(showContactModal(options)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(InsertArticleModal))